import React, { useState } from "react";
import { createUserWithEmailAndPassword, updateProfile, signOut } from "firebase/auth";
import { auth } from "../../../firebase/config";
import { useNavigate } from "react-router-dom";
import apiService from "../../../services/api.service";
import { ROUTES } from "../../../constants";
import { Button } from "../../common/Button";
import { Input } from "../../common/Input";
import { Alert } from "../../common/Alert";
import { TermsModal } from "../TermsModal";
import "../../../assets/css/auth.css";

import { FaUser, FaEnvelope, FaLock, FaUserPlus, FaInfoCircle } from "react-icons/fa";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nombre: "",
    correo: "",
    password: "",
    confirmPassword: "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Validaciones
  const validarCaracteresNombre = (nombre) => {
    const regex = /^[a-zA-ZÀ-ÿñÑ]+(?:[ '-][a-zA-ZÀ-ÿñÑ]+)*$/;
    if (/\d/.test(nombre)) return false;
    if (/[!@#$%^&*()_+=[\]{};":\\|,<>/?~`]/.test(nombre)) return false;
    return regex.test(nombre.trim());
  };

  const validateForm = () => {
    const errors = {};

    if (!form.nombre.trim()) {
      errors.nombre = "El nombre es requerido";
    } else if (form.nombre.trim().length < 2) {
      errors.nombre = "Debe tener al menos 2 caracteres";
    } else if (!validarCaracteresNombre(form.nombre)) {
      errors.nombre = "Solo se permiten letras y acentos";
    }

    if (!form.correo) {
      errors.correo = "El email es requerido";
    } else if (!/\S+@\S+\.\S+/.test(form.correo)) {
      errors.correo = "Email inválido";
    }

    if (!form.password) {
      errors.password = "La contraseña es requerida";
    } else if (form.password.length < 6) {
      errors.password = "Debe tener al menos 6 caracteres";
    }

    if (form.password !== form.confirmPassword) {
      errors.confirmPassword = "Las contraseñas no coinciden";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!acceptedTerms) {
      setError("Debes aceptar los términos y condiciones.");
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    try {
      const { correo, password, nombre } = form;

      // 1. Crear usuario en Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, correo, password);
      const user = userCredential.user;

      // 2. Actualizar perfil en Firebase
      await updateProfile(user, { displayName: nombre.trim() });

      // 3. Sincronizar con Backend
      // Nota: Enviamos null como teléfono ya que no es requerido ahora
      const res = await apiService.syncUser(user.uid, correo, nombre.trim(), null);
      if (!res.ok) throw new Error("Error al sincronizar usuario con backend");

      // 4. Cerrar sesión para obligar a login limpio
      await signOut(auth);

      navigate(ROUTES.LOGIN, {
        state: { message: "¡Cuenta creada exitosamente! Iniciá sesión con tu email y contraseña." },
      });
    } catch (err) {
      console.error("❌ Error en registro:", err);
      let message = "No se pudo completar el registro";
      if (err.code === "auth/email-already-in-use") message = "Ya existe una cuenta con este email";
      else if (err.code === "auth/invalid-email") message = "El email es inválido";
      else if (err.code === "auth/weak-password") message = "La contraseña es muy débil";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = () => {
    setAcceptedTerms(true);
    setShowTerms(false);
  };

  const handleInputChange = (field, value) => {
    if (field === "nombre") {
      const filteredValue = value.replace(/[^a-zA-ZÀ-ÿñÑ\s'.-]/g, "");
      setForm((prev) => ({ ...prev, [field]: filteredValue }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Header */}
        <div className="auth-header">
          <FaUserPlus size={48} color="var(--primary)" style={{ marginBottom: "8px" }} />
          <h1 className="auth-title">
            Crear Cuenta
          </h1>
          <p className="auth-subtitle">
            Completá tus datos para registrarte
          </p>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="error" onClose={() => setError("")} style={{ marginBottom: "20px" }}>
            {error}
          </Alert>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="auth-form">
          <Input
            type="text"
            label="Nombre Completo"
            placeholder="Juan Pérez"
            icon={<FaUser />}
            value={form.nombre}
            onChange={(e) => handleInputChange("nombre", e.target.value)}
            error={validationErrors.nombre}
            disabled={loading}
            required
          />

          <Input
            type="email"
            label="Correo Electrónico"
            placeholder="tu@email.com"
            icon={<FaEnvelope />}
            value={form.correo}
            onChange={(e) => handleInputChange("correo", e.target.value)}
            error={validationErrors.correo}
            disabled={loading}
            required
          />

          <Input
            type="password"
            label="Contraseña"
            placeholder="••••••••"
            icon={<FaLock />}
            value={form.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            error={validationErrors.password}
            disabled={loading}
            required
          />

          <Input
            type="password"
            label="Confirmar Contraseña"
            placeholder="••••••••"
            icon={<FaLock />}
            value={form.confirmPassword}
            onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
            error={validationErrors.confirmPassword}
            disabled={loading}
            required
          />

          {/* Términos */}
          <div className="auth-terms-box">
            <label className="auth-terms-label">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={() => (acceptedTerms ? setAcceptedTerms(false) : setShowTerms(true))}
                style={{ marginTop: "4px" }}
              />
              <span>
                He leído y acepto los{" "}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTerms(true);
                  }}
                  className="auth-link"
                >
                  Términos y Condiciones
                </span>
                {" "}del servicio municipal.
              </span>
            </label>
          </div>

          {/* Botones */}
          <div className="auth-actions">
            <Button
              type="submit"
              fullWidth
              disabled={loading || !acceptedTerms}
              className="btn-auth-primary"
            >
              {loading ? "Creando cuenta..." : "Registrarse"}
            </Button>

            <Button
              type="button"
              onClick={() => navigate(ROUTES.WELCOME)}
              disabled={loading}
              className="btn-auth-secondary"
            >
              Cancelar
            </Button>
          </div>
        </form>

        {/* Info adicional */}
        <div className="auth-info-box">
          <FaInfoCircle /> ¿Ya tenés cuenta? <span
            className="auth-link"
            style={{ marginLeft: "4px" }}
            onClick={() => navigate(ROUTES.LOGIN)}
          >
            Iniciá Sesión
          </span>
        </div>

        <div className="auth-footer">
          <p className="auth-footer-text">
            Sistema Municipal de Gestión de Reclamos
          </p>
        </div>
      </div>

      <TermsModal isVisible={showTerms} onClose={() => setShowTerms(false)} onAccept={handleAcceptTerms} />
    </div>
  );
}
